import React, { useCallback, useMemo } from 'react';
import { VendorCtx } from 'shared/state/configContexts';
import { useVendor } from './legacy';
import { usePermissions } from './PermissionsProvider';

/**
 * Vendor context without the calculation engine, for /admin. TierProvider
 * (mounted above AdminLayout's content) needs a vendor to build its
 * AbilityProvider; the admin pages have no configuration, so mounting
 * ConfigProvider (and its one useConfigurationLogic call) here would run the
 * whole calculation engine on pages that never read a single one of its
 * outputs.
 */
export function VendorOnlyProvider({ children }: { children: React.ReactNode }) {
  const { userGroups, isCheckingAccess } = usePermissions();
  const vendorApi = useVendor({ userGroups, isCheckingAccess });

  // No canSwitchTo guard here on purpose: useVendor.switchVendor already
  // rejects an invalid/inactive vendor and, absent `force`, an id the
  // caller's groups don't grant access to (config/vendorRegistry.js's
  // hasVendorAccess via hooks/useVendor.js). switchVendor is the authority
  // on whether a switch is allowed, so re-checking it here would just be a
  // second, redundant gate.
  const changeVendor = useCallback((id: string) => { vendorApi.switchVendor(id); }, [vendorApi]);
  const value = useMemo(() => ({ ...vendorApi, changeVendor }), [vendorApi, changeVendor]);

  return <VendorCtx.Provider value={value}>{children}</VendorCtx.Provider>;
}
